/**
 * Section counts that match the results a viewer can actually open
 * (issue #168).
 *
 * `/api/listings/counts?category=…` returns one row per stored subcategory,
 * computed by the server from the SAME visibility rules and filters as
 * `/api/listings` (storage.getListingFacetCounts). Each row splits its items
 * into `base` (ads + store own products) and `resale` (resale copies,
 * distinct per wholesale listing). Resale copies are a curated strip:
 * `/api/listings` shows at most RESALE_VISIBLE_CAP of them for any query, so
 * a section's number caps them for that section exactly like opening the
 * section does.
 *
 * Rows whose subcategory is empty, or is a name that is not a leaf of the
 * category tree (old or free-text names), are UNCLASSIFIED: they belong to
 * the category total and are reachable through an explicit
 * «بلا قسم فرعي» entry (`/api/listings?category=…&unclassified=1`). They are
 * never moved into a branch by guessing.
 *
 * Pure (no React, no DB): used by the desktop sidebar, the mobile drill-down
 * and the server tests.
 */

import { CATEGORY_HIERARCHIES, getAllSubcategories, type CategoryNode } from "./categoryHierarchy";
import { canonicalizeSubcategory, getSubcategoryAliases } from "./constants";

/** Most resale copies `/api/listings` shows for one query (deduped per wholesale listing). */
export const RESALE_VISIBLE_CAP = 6;

export interface SubcategoryCountRow {
  category: string;
  subcategory: string | null;
  /** Visible items in this subcategory alone: base + min(resale, cap). */
  count: number;
  /** Ads + store own products. Absent on older servers (then `count` is used). */
  base?: number;
  /** Resale copies, distinct per wholesale listing, before the cap. */
  resale?: number;
}

/** Leaf subcategory names of a category tree (canonical names). */
export function categoryLeaves(categoryKey: string): string[] {
  const root = CATEGORY_HIERARCHIES[categoryKey];
  return root ? Array.from(new Set(getAllSubcategories(root))) : [];
}

/** Every stored value that means one of the category's leaves (leaves + legacy aliases). */
export function categoryLeafValues(categoryKey: string): string[] {
  const out = new Set<string>();
  for (const leaf of categoryLeaves(categoryKey)) {
    out.add(leaf);
    for (const alias of getSubcategoryAliases(leaf)) out.add(alias);
  }
  return Array.from(out);
}

/** Visible number for a set of rows: base + resale capped once for the whole set. */
export function visibleCount(rows: readonly SubcategoryCountRow[]): number {
  let base = 0;
  let resale = 0;
  for (const row of rows) {
    base += row.base ?? row.count;
    resale += row.resale ?? 0;
  }
  return base + Math.min(resale, RESALE_VISIBLE_CAP);
}

export interface CategoryCounts {
  /** Everything the viewer can open in the category (= `/api/listings` total). */
  total: number;
  /** Items in no leaf of the tree (empty or unknown subcategory). */
  unclassified: number;
  /** Count for a tree node (a branch or a named leaf node). */
  node(node: CategoryNode): number;
  /** Count for one leaf subcategory name. */
  leaf(subcategory: string): number;
}

export function buildCategoryCounts(categoryKey: string, rows: readonly SubcategoryCountRow[] | null | undefined): CategoryCounts {
  const safeRows = Array.isArray(rows) ? rows : [];
  const leafSet = new Set(categoryLeaves(categoryKey));
  const byLeaf = new Map<string, SubcategoryCountRow[]>();
  const unclassifiedRows: SubcategoryCountRow[] = [];
  for (const row of safeRows) {
    // Exact stored value, like the results' `subcategory = …` filter: a
    // padded or misspelt name is not a leaf, so it is «بلا قسم فرعي».
    const sub = canonicalizeSubcategory(row?.subcategory ?? null);
    if (sub && leafSet.has(sub)) {
      const list = byLeaf.get(sub) ?? [];
      list.push(row);
      byLeaf.set(sub, list);
    } else {
      unclassifiedRows.push(row);
    }
  }
  const forLeaves = (leaves: Iterable<string>) => {
    const picked: SubcategoryCountRow[] = [];
    for (const leaf of Array.from(new Set(leaves))) picked.push(...(byLeaf.get(leaf) ?? []));
    return visibleCount(picked);
  };
  return {
    total: visibleCount(safeRows),
    unclassified: visibleCount(unclassifiedRows),
    node: (node) => forLeaves(getAllSubcategories(node)),
    leaf: (subcategory) => forLeaves([canonicalizeSubcategory(subcategory)]),
  };
}
